/**
 * Hand-written structural validator for card data.
 *
 * This is the single source of truth for "is this JSON a legal card?". The CLI
 * (scripts/validate-cards.ts) and the Test Lab error panel both use it, so an
 * author sees the same message in both places.
 *
 * Raw input is `unknown` and narrowed by hand. This file is the only place in
 * the project allowed to use `any`-adjacent parsing helpers.
 */

import {
  ALL_KEYWORDS,
  type CardDefinition,
  type DeckDefinition,
  type TokenDefinition,
} from '../engine/cardTypes';

// ---------------------------------------------------------------------------
// Vocabulary. Must match src/engine/cardTypes.ts exactly.
// ---------------------------------------------------------------------------

export const FACTIONS = ['btc', 'eth', 'sol', 'bnb', 'xrp', 'neutral'] as const;
export const CHAINS = ['btc', 'eth', 'sol', 'bnb', 'xrp'] as const;
export const CARD_TYPES = ['character', 'wallet', 'transaction', 'exploit', 'protocol', 'upgrade'] as const;
export const RARITIES = ['common', 'uncommon', 'rare', 'genesis'] as const;

export const SUBTYPES = [
  'Founder', 'Developer', 'Trader', 'Degen', 'Whale', 'Miner', 'Validator',
  'KOL', 'Influencer', 'Hacker', 'Builder', 'MarketMaker', 'Bot', 'Troll',
  'Maxi', 'Farmer', 'Rugger', 'Sniper', 'Banker', 'Lawyer', 'Regulator',
  'OG', 'Army',
  'Meme', 'Coin', 'Token', 'NFT', 'DAO', 'Node', 'DApp', 'Oracle',
  'Mascot', 'Exchange', 'Bridge', 'Contract', 'Event', 'Hardware', 'Status',
] as const;

export const EFFECT_OPS = [
  'damage', 'destroy', 'draw', 'discard', 'gainIntegrity', 'loseIntegrity',
  'buff', 'grantKeyword', 'removeKeyword', 'createToken', 'exhaust', 'ready',
  'freeze', 'bounce', 'recover', 'fetchWallet', 'addGas', 'revert', 'blacklist',
  'mill', 'sacrifice', 'scry', 'conditional', 'sequence',
] as const;

export const TRIGGER_EVENTS = [
  'self:deploy', 'self:death', 'self:attack', 'self:block', 'self:damagePlayer',
  'self:survivesDamage', 'turn:start', 'turn:end', 'character:deploy',
  'character:death', 'wallet:install', 'spell:play', 'integrity:gain', 'token:create',
] as const;

export const STATIC_TYPES = [
  'buff', 'grantKeyword', 'costModifier', 'cantBlock', 'cantBeBlockedBy',
  'walletsProduceExtra', 'extraWalletInstall', 'handSizeModifier',
  'damageModifier', 'opponentCharactersEnterExhausted', 'attackRequirement',
] as const;

export const TARGET_TYPES = [
  'character', 'wallet', 'protocol', 'upgrade', 'permanent', 'player', 'any',
  'stackItem', 'cardInDust', 'cardInHand',
] as const;

export const TARGET_MODES = [
  'choose', 'all', 'random', 'self', 'host', 'controller', 'opponent', 'triggering',
] as const;

export const FILTER_FIELDS = [
  'types', 'subtypes', 'keywords', 'lacksKeywords', 'faction', 'minCost', 'maxCost',
  'minAttack', 'maxAttack', 'minHealth', 'maxHealth', 'isToken', 'isLegendary',
  'exhausted', 'attacking', 'blocking', 'damaged', 'excludeSelf',
] as const;

export const CONDITION_KINDS = [
  'control', 'integrity', 'turn', 'dust', 'handSize', 'wallets',
  'playedCardThisTurn', 'sourceDamaged', 'not', 'all', 'any',
] as const;

export const AMOUNT_KINDS = [
  'count', 'countDust', 'handSize', 'walletCount', 'sourceAttack', 'sourceHealth', 'x',
] as const;

export const AI_ROLES = [
  'aggro', 'midrange', 'control', 'ramp', 'removal', 'draw', 'protection',
  'finisher', 'utility', 'wallet', 'sweeper', 'revert', 'token',
] as const;

export const AI_PLAY_WHEN = [
  'asap', 'onCurve', 'hasTarget', 'behind', 'ahead', 'lethal', 'response',
] as const;

export const WHO_VALUES = ['friendly', 'enemy', 'any'] as const;
export const DURATIONS = ['turn', 'permanent'] as const;
export const COST_KEYS = ['generic', 'btc', 'eth', 'sol', 'bnb', 'xrp', 'x'] as const;
export const ART_STATUS = ['placeholder', 'sketch', 'final'] as const;
export const LEGALITY_VALUES = ['legal', 'banned', 'restricted'] as const;

// ---------------------------------------------------------------------------
// Issue collection
// ---------------------------------------------------------------------------

export type IssueLevel = 'error' | 'warning';

export interface Issue {
  level: IssueLevel;
  /** Dotted path inside the card, e.g. "abilities[0].effects[1].target.mode". */
  path: string;
  message: string;
  /** Card or token id when known, otherwise the file and index. */
  subject: string;
}

class Collector {
  readonly issues: Issue[] = [];
  constructor(private subject: string) {}

  setSubject(subject: string): void {
    this.subject = subject;
  }

  error(path: string, message: string): void {
    this.issues.push({ level: 'error', path, message, subject: this.subject });
  }

  warn(path: string, message: string): void {
    this.issues.push({ level: 'warning', path, message, subject: this.subject });
  }
}

type Raw = Record<string, unknown>;

function isObject(v: unknown): v is Raw {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function isNumber(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v);
}

function isString(v: unknown): v is string {
  return typeof v === 'string';
}

function inSet<T extends readonly string[]>(v: unknown, set: T): boolean {
  return isString(v) && (set as readonly string[]).includes(v);
}

// ---------------------------------------------------------------------------
// Sub-validators
// ---------------------------------------------------------------------------

function checkFilter(c: Collector, raw: unknown, path: string): void {
  if (raw === undefined) return;
  if (!isObject(raw)) {
    c.error(path, 'filter must be an object.');
    return;
  }
  for (const key of Object.keys(raw)) {
    if (!(FILTER_FIELDS as readonly string[]).includes(key)) {
      c.error(`${path}.${key}`, `Unknown filter field "${key}".`);
    }
  }
  const arrayOf = (key: string, set: readonly string[]) => {
    const v = raw[key];
    if (v === undefined) return;
    if (!Array.isArray(v) || v.length === 0) {
      c.error(`${path}.${key}`, `${key} must be a non-empty array.`);
      return;
    }
    for (const item of v) {
      if (!inSet(item, set)) c.error(`${path}.${key}`, `Unknown value "${String(item)}".`);
    }
  };
  arrayOf('types', CARD_TYPES);
  arrayOf('subtypes', SUBTYPES);
  arrayOf('keywords', ALL_KEYWORDS);
  arrayOf('lacksKeywords', ALL_KEYWORDS);
  arrayOf('faction', FACTIONS);
  for (const key of ['minCost', 'maxCost', 'minAttack', 'maxAttack', 'minHealth', 'maxHealth']) {
    const v = raw[key];
    if (v !== undefined && !isNumber(v)) c.error(`${path}.${key}`, `${key} must be a number.`);
  }
  for (const key of ['isToken', 'isLegendary', 'exhausted', 'attacking', 'blocking', 'damaged', 'excludeSelf']) {
    const v = raw[key];
    if (v !== undefined && typeof v !== 'boolean') c.error(`${path}.${key}`, `${key} must be a boolean.`);
  }
}

function checkTarget(c: Collector, raw: unknown, path: string): void {
  if (!isObject(raw)) {
    c.error(path, 'target must be an object.');
    return;
  }
  if (!inSet(raw.type, TARGET_TYPES)) {
    c.error(`${path}.type`, `Unknown target type "${String(raw.type)}".`);
  }
  if (!inSet(raw.mode, TARGET_MODES)) {
    c.error(`${path}.mode`, `Unknown target mode "${String(raw.mode)}".`);
  }
  if (raw.who !== undefined && !inSet(raw.who, WHO_VALUES)) {
    c.error(`${path}.who`, `Unknown who "${String(raw.who)}".`);
  }
  if (raw.count !== undefined && (!isNumber(raw.count) || raw.count < 1)) {
    c.error(`${path}.count`, 'count must be a positive number.');
  }
  if (raw.optional !== undefined && typeof raw.optional !== 'boolean') {
    c.error(`${path}.optional`, 'optional must be a boolean.');
  }
  if (raw.slot !== undefined) {
    if (!isString(raw.slot) || raw.slot.trim().length === 0) {
      c.error(`${path}.slot`, 'slot must be a non-empty string.');
    } else if (raw.mode !== 'choose') {
      c.error(`${path}.slot`, 'slot only applies to mode "choose".');
    }
  }
  checkFilter(c, raw.filter, `${path}.filter`);

  // Mode and type agreement.
  if (raw.mode === 'host' && raw.type !== 'character') {
    c.error(`${path}.mode`, 'mode "host" only makes sense with type "character".');
  }
  if ((raw.mode === 'controller' || raw.mode === 'opponent') && raw.type !== 'player') {
    c.error(`${path}.mode`, `mode "${String(raw.mode)}" requires type "player".`);
  }
  if (raw.type === 'stackItem' && raw.mode !== 'choose' && raw.mode !== 'all' && raw.mode !== 'random') {
    c.error(`${path}.mode`, 'stackItem targets use choose, all or random.');
  }
}

function checkAmount(c: Collector, raw: unknown, path: string): void {
  if (isNumber(raw)) return;
  if (!isObject(raw)) {
    c.error(path, 'amount must be a number or an amount object.');
    return;
  }
  if (!inSet(raw.kind, AMOUNT_KINDS)) {
    c.error(`${path}.kind`, `Unknown amount kind "${String(raw.kind)}".`);
    return;
  }
  switch (raw.kind) {
    case 'count':
    case 'countDust':
      if (!inSet(raw.who, WHO_VALUES)) c.error(`${path}.who`, 'who must be friendly, enemy or any.');
      if (!isObject(raw.filter)) c.error(`${path}.filter`, 'filter is required.');
      else checkFilter(c, raw.filter, `${path}.filter`);
      for (const key of ['times', 'plus']) {
        if (raw[key] !== undefined && !isNumber(raw[key])) c.error(`${path}.${key}`, `${key} must be a number.`);
      }
      break;
    case 'handSize':
      if (raw.who !== 'controller' && raw.who !== 'opponent') {
        c.error(`${path}.who`, 'who must be controller or opponent.');
      }
      break;
    case 'walletCount':
      if (raw.who !== 'controller' && raw.who !== 'opponent') {
        c.error(`${path}.who`, 'who must be controller or opponent.');
      }
      if (raw.chain !== undefined && !inSet(raw.chain, CHAINS)) {
        c.error(`${path}.chain`, `Unknown chain "${String(raw.chain)}".`);
      }
      break;
    default:
      break;
  }
}

function checkCondition(c: Collector, raw: unknown, path: string): void {
  if (!isObject(raw)) {
    c.error(path, 'condition must be an object.');
    return;
  }
  if (!inSet(raw.kind, CONDITION_KINDS)) {
    c.error(`${path}.kind`, `Unknown condition kind "${String(raw.kind)}".`);
    return;
  }
  const range = () => {
    for (const key of ['min', 'max']) {
      if (raw[key] !== undefined && !isNumber(raw[key])) c.error(`${path}.${key}`, `${key} must be a number.`);
    }
  };
  switch (raw.kind) {
    case 'control':
    case 'dust':
      if (!inSet(raw.who, WHO_VALUES)) c.error(`${path}.who`, 'who must be friendly, enemy or any.');
      if (!isObject(raw.filter)) c.error(`${path}.filter`, 'filter is required.');
      else checkFilter(c, raw.filter, `${path}.filter`);
      range();
      break;
    case 'integrity':
    case 'handSize':
      if (raw.who !== 'controller' && raw.who !== 'opponent') {
        c.error(`${path}.who`, 'who must be controller or opponent.');
      }
      range();
      break;
    case 'wallets':
      if (raw.who !== 'controller' && raw.who !== 'opponent') {
        c.error(`${path}.who`, 'who must be controller or opponent.');
      }
      if (raw.chain !== undefined && !inSet(raw.chain, CHAINS)) {
        c.error(`${path}.chain`, `Unknown chain "${String(raw.chain)}".`);
      }
      range();
      break;
    case 'turn':
      range();
      break;
    case 'playedCardThisTurn':
      if (raw.who !== 'controller') c.error(`${path}.who`, 'who must be controller.');
      if (raw.types !== undefined) {
        if (!Array.isArray(raw.types)) c.error(`${path}.types`, 'types must be an array.');
        else {
          for (const t of raw.types) {
            if (!inSet(t, CARD_TYPES)) c.error(`${path}.types`, `Unknown card type "${String(t)}".`);
          }
        }
      }
      if (raw.min !== undefined && !isNumber(raw.min)) c.error(`${path}.min`, 'min must be a number.');
      break;
    case 'sourceDamaged':
      break;
    case 'not':
      checkCondition(c, raw.condition, `${path}.condition`);
      break;
    case 'all':
    case 'any':
      if (!Array.isArray(raw.conditions) || raw.conditions.length === 0) {
        c.error(`${path}.conditions`, 'conditions must be a non-empty array.');
      } else {
        raw.conditions.forEach((cond, i) => checkCondition(c, cond, `${path}.conditions[${i}]`));
      }
      break;
    default:
      break;
  }
}

interface EffectContext {
  /** Token ids this card declares, so createToken can be cross-checked. */
  declaredTokens: string[];
  /** Token ids actually referenced, filled in by the checker. */
  usedTokens: Set<string>;
  /** True when the card declares cost.x, so `{ kind: 'x' }` is allowed. */
  hasX: boolean;
}

function checkEffect(c: Collector, raw: unknown, path: string, ec: EffectContext): void {
  if (!isObject(raw)) {
    c.error(path, 'effect must be an object.');
    return;
  }
  const op = raw.op;
  if (!inSet(op, EFFECT_OPS)) {
    c.error(`${path}.op`, `Unknown effect op "${String(op)}".`);
    return;
  }
  const needTarget = (types?: readonly string[]) => {
    checkTarget(c, raw.target, `${path}.target`);
    if (types && isObject(raw.target) && isString(raw.target.type) && !types.includes(raw.target.type)) {
      c.error(`${path}.target.type`, `op "${op}" requires target.type in ${types.join(', ')}.`);
    }
  };
  const needAmount = () => {
    checkAmount(c, raw.amount, `${path}.amount`);
    if (isObject(raw.amount) && raw.amount.kind === 'x' && !ec.hasX) {
      c.error(`${path}.amount`, 'amount { kind: "x" } needs cost.x = true on the card.');
    }
  };
  const needWho = (allowed: readonly string[]) => {
    if (!isString(raw.who) || !allowed.includes(raw.who)) {
      c.error(`${path}.who`, `who must be one of ${allowed.join(', ')}.`);
    }
  };

  switch (op) {
    case 'damage':
      needAmount();
      needTarget(['character', 'player', 'any', 'permanent']);
      break;
    case 'destroy':
      needTarget(['character', 'permanent', 'protocol', 'upgrade', 'wallet']);
      break;
    case 'draw':
      needAmount();
      needWho(['controller', 'opponent', 'each']);
      break;
    case 'discard':
      needAmount();
      needWho(['controller', 'opponent']);
      if (raw.random !== undefined && typeof raw.random !== 'boolean') {
        c.error(`${path}.random`, 'random must be a boolean.');
      }
      break;
    case 'gainIntegrity':
    case 'loseIntegrity':
    case 'mill':
      needAmount();
      needWho(['controller', 'opponent']);
      break;
    case 'buff':
      if (!isNumber(raw.attack)) c.error(`${path}.attack`, 'attack must be a number.');
      if (!isNumber(raw.health)) c.error(`${path}.health`, 'health must be a number.');
      if (!inSet(raw.duration, DURATIONS)) c.error(`${path}.duration`, 'duration must be turn or permanent.');
      needTarget(['character', 'permanent']);
      break;
    case 'grantKeyword':
    case 'removeKeyword':
      if (!inSet(raw.keyword, ALL_KEYWORDS)) {
        c.error(`${path}.keyword`, `Unknown keyword "${String(raw.keyword)}".`);
      }
      if (!inSet(raw.duration, DURATIONS)) c.error(`${path}.duration`, 'duration must be turn or permanent.');
      needTarget(['character', 'permanent']);
      break;
    case 'createToken':
      if (!isString(raw.token) || raw.token.length === 0) {
        c.error(`${path}.token`, 'token must be a token id.');
      } else {
        ec.usedTokens.add(raw.token);
        if (!ec.declaredTokens.includes(raw.token)) {
          c.error(`${path}.token`, `Token "${raw.token}" must also be listed in the card's tokens array.`);
        }
      }
      needAmount();
      if (raw.who !== undefined && raw.who !== 'controller' && raw.who !== 'opponent') {
        c.error(`${path}.who`, 'who must be controller or opponent.');
      }
      break;
    case 'exhaust':
    case 'ready':
    case 'freeze':
      needTarget(['character', 'permanent', 'wallet']);
      break;
    case 'bounce':
      needTarget(['character', 'permanent', 'protocol', 'upgrade', 'wallet']);
      break;
    case 'recover':
      needTarget(['cardInDust']);
      if (raw.to !== 'hand' && raw.to !== 'battlefield') {
        c.error(`${path}.to`, 'to must be hand or battlefield.');
      }
      break;
    case 'fetchWallet':
      if (raw.chain !== undefined && raw.chain !== 'any' && !inSet(raw.chain, CHAINS)) {
        c.error(`${path}.chain`, `Unknown chain "${String(raw.chain)}".`);
      }
      if (raw.to !== 'hand' && raw.to !== 'battlefield') {
        c.error(`${path}.to`, 'to must be hand or battlefield.');
      }
      if (raw.exhausted !== undefined && typeof raw.exhausted !== 'boolean') {
        c.error(`${path}.exhausted`, 'exhausted must be a boolean.');
      }
      break;
    case 'addGas':
      if (raw.chain !== 'any' && !inSet(raw.chain, CHAINS)) {
        c.error(`${path}.chain`, `Unknown chain "${String(raw.chain)}".`);
      }
      needAmount();
      break;
    case 'revert':
      needTarget(['stackItem']);
      break;
    case 'blacklist':
      needTarget(['character', 'permanent', 'cardInDust', 'protocol', 'upgrade', 'wallet']);
      break;
    case 'sacrifice':
      needTarget(['character', 'permanent', 'wallet', 'protocol', 'upgrade']);
      if (isObject(raw.target) && raw.target.who === 'enemy') {
        c.error(`${path}.target.who`, 'sacrifice only ever hits the controller’s own permanents.');
      }
      break;
    case 'scry':
      needAmount();
      break;
    case 'conditional':
      checkCondition(c, raw.condition, `${path}.condition`);
      if (!Array.isArray(raw.then) || raw.then.length === 0) {
        c.error(`${path}.then`, 'then must be a non-empty array of effects.');
      } else {
        raw.then.forEach((e, i) => checkEffect(c, e, `${path}.then[${i}]`, ec));
      }
      if (raw.else !== undefined) {
        if (!Array.isArray(raw.else)) c.error(`${path}.else`, 'else must be an array of effects.');
        else raw.else.forEach((e, i) => checkEffect(c, e, `${path}.else[${i}]`, ec));
      }
      break;
    case 'sequence':
      if (!Array.isArray(raw.effects) || raw.effects.length === 0) {
        c.error(`${path}.effects`, 'effects must be a non-empty array.');
      } else {
        raw.effects.forEach((e, i) => checkEffect(c, e, `${path}.effects[${i}]`, ec));
      }
      break;
    default:
      break;
  }
}

function checkCost(c: Collector, raw: unknown, path: string, allowEmpty: boolean): void {
  if (!isObject(raw)) {
    c.error(path, 'cost must be an object.');
    return;
  }
  const keys = Object.keys(raw);
  for (const key of keys) {
    if (!(COST_KEYS as readonly string[]).includes(key)) {
      c.error(`${path}.${key}`, `Unknown cost key "${key}".`);
      continue;
    }
    if (key === 'x') {
      if (typeof raw.x !== 'boolean') c.error(`${path}.x`, 'cost.x must be a boolean.');
      continue;
    }
    const v = raw[key];
    if (!isNumber(v) || v < 0 || !Number.isInteger(v)) {
      c.error(`${path}.${key}`, `${key} must be a non-negative integer.`);
    }
  }
  if (!allowEmpty && keys.filter((k) => k !== 'x').length === 0) {
    c.warn(path, 'cost is empty, so this card is free.');
  }
}

/**
 * Choose targets that share a `slot` name inside one ability are one prompt, so
 * every occurrence must describe the same choice. This walks the whole effect
 * tree of one ability and reports any slot name whose occurrences disagree.
 */
function checkSharedSlots(c: Collector, effects: unknown, path: string): void {
  if (!Array.isArray(effects)) return;
  const seen = new Map<string, { signature: string; path: string }>();

  const signatureOf = (target: Raw): string =>
    JSON.stringify({
      type: target.type ?? null,
      who: target.who ?? 'any',
      mode: target.mode ?? null,
      count: target.count ?? 1,
      optional: target.optional === true,
      filter: target.filter ?? null,
    });

  const walk = (list: unknown[], at: string): void => {
    list.forEach((effect, i) => {
      if (!isObject(effect)) return;
      const here = `${at}[${i}]`;
      if (effect.op === 'conditional') {
        if (Array.isArray(effect.then)) walk(effect.then, `${here}.then`);
        if (Array.isArray(effect.else)) walk(effect.else, `${here}.else`);
        return;
      }
      if (effect.op === 'sequence') {
        if (Array.isArray(effect.effects)) walk(effect.effects, `${here}.effects`);
        return;
      }
      const target = effect.target;
      if (!isObject(target)) return;
      if (target.mode !== 'choose') return;
      if (!isString(target.slot) || target.slot.length === 0) return;
      const signature = signatureOf(target);
      const previous = seen.get(target.slot);
      if (!previous) {
        seen.set(target.slot, { signature, path: `${here}.target` });
        return;
      }
      if (previous.signature !== signature) {
        c.error(
          `${here}.target.slot`,
          `slot "${target.slot}" is shared with ${previous.path}, so both targets must have the same type, who, mode, count, optional and filter.`,
        );
      }
    });
  };

  walk(effects, path);
}

function checkAbility(c: Collector, raw: unknown, path: string, ec: EffectContext): void {
  if (!isObject(raw)) {
    c.error(path, 'ability must be an object.');
    return;
  }
  const kind = raw.kind;
  if (kind !== 'triggered' && kind !== 'activated' && kind !== 'static') {
    c.error(`${path}.kind`, `Unknown ability kind "${String(kind)}".`);
    return;
  }
  if (kind === 'triggered') {
    if (!inSet(raw.event, TRIGGER_EVENTS)) {
      c.error(`${path}.event`, `Unknown trigger event "${String(raw.event)}".`);
    }
    if (raw.who !== undefined && !inSet(raw.who, WHO_VALUES)) {
      c.error(`${path}.who`, 'who must be friendly, enemy or any.');
    }
    checkFilter(c, raw.filter, `${path}.filter`);
    if (raw.condition !== undefined) checkCondition(c, raw.condition, `${path}.condition`);
    if (raw.oncePerTurn !== undefined && typeof raw.oncePerTurn !== 'boolean') {
      c.error(`${path}.oncePerTurn`, 'oncePerTurn must be a boolean.');
    }
    if (!Array.isArray(raw.effects) || raw.effects.length === 0) {
      c.error(`${path}.effects`, 'effects must be a non-empty array.');
    } else {
      raw.effects.forEach((e, i) => checkEffect(c, e, `${path}.effects[${i}]`, ec));
      checkSharedSlots(c, raw.effects, `${path}.effects`);
    }
    return;
  }
  if (kind === 'activated') {
    if (raw.speed !== 'main' && raw.speed !== 'any') {
      c.error(`${path}.speed`, 'speed must be main or any.');
    }
    if (!isObject(raw.cost)) {
      c.error(`${path}.cost`, 'activated abilities need a cost object (use {} for free).');
    } else {
      const cost = raw.cost;
      const allowed = ['gas', 'exhaust', 'sacrificeSelf', 'sacrifice', 'discard', 'payIntegrity', 'removeFromDust'];
      for (const key of Object.keys(cost)) {
        if (!allowed.includes(key)) c.error(`${path}.cost.${key}`, `Unknown ability cost "${key}".`);
      }
      if (cost.gas !== undefined) checkCost(c, cost.gas, `${path}.cost.gas`, true);
      for (const key of ['exhaust', 'sacrificeSelf', 'removeFromDust']) {
        if (cost[key] !== undefined && typeof cost[key] !== 'boolean') {
          c.error(`${path}.cost.${key}`, `${key} must be a boolean.`);
        }
      }
      if (cost.sacrifice !== undefined) checkFilter(c, cost.sacrifice, `${path}.cost.sacrifice`);
      for (const key of ['discard', 'payIntegrity']) {
        const v = cost[key];
        if (v !== undefined && (!isNumber(v) || v < 1)) {
          c.error(`${path}.cost.${key}`, `${key} must be a positive number.`);
        }
      }
    }
    if (raw.oncePerTurn !== undefined && typeof raw.oncePerTurn !== 'boolean') {
      c.error(`${path}.oncePerTurn`, 'oncePerTurn must be a boolean.');
    }
    if (raw.label !== undefined && !isString(raw.label)) {
      c.error(`${path}.label`, 'label must be a string.');
    }
    if (!Array.isArray(raw.effects) || raw.effects.length === 0) {
      c.error(`${path}.effects`, 'effects must be a non-empty array.');
    } else {
      raw.effects.forEach((e, i) => checkEffect(c, e, `${path}.effects[${i}]`, ec));
      checkSharedSlots(c, raw.effects, `${path}.effects`);
    }
    return;
  }
  // static
  if (raw.condition !== undefined) checkCondition(c, raw.condition, `${path}.condition`);
  const effect = raw.effect;
  if (!isObject(effect)) {
    c.error(`${path}.effect`, 'static abilities need an effect object.');
    return;
  }
  if (!inSet(effect.type, STATIC_TYPES)) {
    c.error(`${path}.effect.type`, `Unknown static type "${String(effect.type)}".`);
    return;
  }
  const ep = `${path}.effect`;
  switch (effect.type) {
    case 'buff':
      if (!inSet(effect.who, WHO_VALUES)) c.error(`${ep}.who`, 'who must be friendly, enemy or any.');
      if (!isObject(effect.filter)) c.error(`${ep}.filter`, 'filter is required.');
      else checkFilter(c, effect.filter, `${ep}.filter`);
      if (!isNumber(effect.attack)) c.error(`${ep}.attack`, 'attack must be a number.');
      if (!isNumber(effect.health)) c.error(`${ep}.health`, 'health must be a number.');
      break;
    case 'grantKeyword':
      if (!inSet(effect.who, WHO_VALUES)) c.error(`${ep}.who`, 'who must be friendly, enemy or any.');
      if (!isObject(effect.filter)) c.error(`${ep}.filter`, 'filter is required.');
      else checkFilter(c, effect.filter, `${ep}.filter`);
      if (!inSet(effect.keyword, ALL_KEYWORDS)) {
        c.error(`${ep}.keyword`, `Unknown keyword "${String(effect.keyword)}".`);
      }
      break;
    case 'costModifier':
      if (!inSet(effect.who, WHO_VALUES)) c.error(`${ep}.who`, 'who must be friendly, enemy or any.');
      if (!isObject(effect.filter)) c.error(`${ep}.filter`, 'filter is required.');
      else checkFilter(c, effect.filter, `${ep}.filter`);
      if (!isNumber(effect.amount)) c.error(`${ep}.amount`, 'amount must be a number.');
      break;
    case 'cantBlock':
    case 'opponentCharactersEnterExhausted':
      break;
    case 'cantBeBlockedBy':
    case 'attackRequirement':
      if (!isObject(effect.filter)) c.error(`${ep}.filter`, 'filter is required.');
      else checkFilter(c, effect.filter, `${ep}.filter`);
      break;
    case 'walletsProduceExtra':
      if (!inSet(effect.chain, CHAINS)) c.error(`${ep}.chain`, `Unknown chain "${String(effect.chain)}".`);
      if (!isNumber(effect.amount)) c.error(`${ep}.amount`, 'amount must be a number.');
      break;
    case 'extraWalletInstall':
    case 'handSizeModifier':
      if (!isNumber(effect.amount)) c.error(`${ep}.amount`, 'amount must be a number.');
      break;
    case 'damageModifier':
      if (effect.who !== 'friendly' && effect.who !== 'enemy') {
        c.error(`${ep}.who`, 'who must be friendly or enemy.');
      }
      if (!isObject(effect.filter)) c.error(`${ep}.filter`, 'filter is required.');
      else checkFilter(c, effect.filter, `${ep}.filter`);
      if (!isNumber(effect.amount)) c.error(`${ep}.amount`, 'amount must be a number.');
      break;
    default:
      break;
  }
}

function checkArt(c: Collector, raw: unknown, path: string): void {
  if (!isObject(raw)) {
    c.error(path, 'art is required and must be an object with path, artist and status.');
    return;
  }
  if (!isString(raw.path) || raw.path.trim().length === 0) {
    c.error(`${path}.path`, 'art.path is required and must be a non-empty string.');
  } else if (raw.path.startsWith('/')) {
    c.error(`${path}.path`, 'art.path is relative to public/, so it must not start with a slash.');
  }
  if (!isString(raw.artist) || raw.artist.trim().length === 0) {
    c.error(`${path}.artist`, 'art.artist is required (use "placeholder" until final).');
  }
  if (!inSet(raw.status, ART_STATUS)) {
    c.error(`${path}.status`, 'art.status must be placeholder, sketch or final.');
  }
}

function checkAi(c: Collector, raw: unknown, path: string): void {
  if (!isObject(raw)) {
    c.error(path, 'ai hints are required (role, value, removalPriority).');
    return;
  }
  if (!inSet(raw.role, AI_ROLES)) {
    c.error(`${path}.role`, `Unknown ai.role "${String(raw.role)}".`);
  }
  for (const key of ['value', 'removalPriority']) {
    const v = raw[key];
    if (!isNumber(v)) {
      c.error(`${path}.${key}`, `ai.${key} is required and must be a number.`);
    } else if (v < 1 || v > 10) {
      c.error(`${path}.${key}`, `ai.${key} must be between 1 and 10 (found ${v}).`);
    }
  }
  if (raw.playWhen !== undefined && !inSet(raw.playWhen, AI_PLAY_WHEN)) {
    c.error(`${path}.playWhen`, `Unknown ai.playWhen "${String(raw.playWhen)}".`);
  }
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export interface ValidateCardOptions {
  /** Token ids that exist, so tokens[] and createToken can be checked. */
  knownTokenIds?: string[];
}

export function validateCard(raw: unknown, where: string, options: ValidateCardOptions = {}): Issue[] {
  const c = new Collector(where);
  if (!isObject(raw)) {
    c.error('', 'A card must be a JSON object.');
    return c.issues;
  }
  const id = isString(raw.id) ? raw.id : undefined;
  c.setSubject(id ?? where);

  if (!id || id.trim().length === 0) c.error('id', 'id is required.');
  else if (!/^[a-z0-9]+(-[a-z0-9]+)+$/.test(id)) {
    c.error('id', 'id must be lowercase kebab-case, like gen-btc-001.');
  }
  if (!isString(raw.name) || raw.name.trim().length === 0) c.error('name', 'name is required.');
  if (!isString(raw.set) || raw.set.trim().length === 0) c.error('set', 'set is required.');
  if (!isNumber(raw.collectorNumber) || raw.collectorNumber < 1) {
    c.error('collectorNumber', 'collectorNumber must be a positive number.');
  }
  if (!inSet(raw.rarity, RARITIES)) c.error('rarity', `Unknown rarity "${String(raw.rarity)}".`);
  if (!inSet(raw.faction, FACTIONS)) c.error('faction', `Unknown faction "${String(raw.faction)}".`);
  if (!inSet(raw.type, CARD_TYPES)) c.error('type', `Unknown card type "${String(raw.type)}".`);
  const type = isString(raw.type) ? raw.type : '';

  if (!Array.isArray(raw.subtypes)) c.error('subtypes', 'subtypes must be an array (use []).');
  else {
    for (const s of raw.subtypes) {
      if (!inSet(s, SUBTYPES)) c.error('subtypes', `Unknown subtype "${String(s)}".`);
    }
  }

  checkCost(c, raw.cost, 'cost', type === 'wallet');
  const hasX = isObject(raw.cost) && raw.cost.x === true;

  if (!Array.isArray(raw.keywords)) c.error('keywords', 'keywords must be an array (use []).');
  else {
    for (const k of raw.keywords) {
      if (!inSet(k, ALL_KEYWORDS)) c.error('keywords', `Unknown keyword "${String(k)}".`);
    }
    if (type !== 'character' && raw.keywords.length > 0) {
      c.error('keywords', 'only Characters carry printed keywords. Use upgrade.keywords or a static.');
    }
  }

  const declaredTokens = Array.isArray(raw.tokens) ? raw.tokens.filter(isString) : [];
  if (raw.tokens !== undefined && !Array.isArray(raw.tokens)) {
    c.error('tokens', 'tokens must be an array of token ids.');
  }
  for (const t of declaredTokens) {
    if (options.knownTokenIds && !options.knownTokenIds.includes(t)) {
      c.error('tokens', `Token "${t}" is not defined in tokens.json.`);
    }
  }

  const ec: EffectContext = { declaredTokens, usedTokens: new Set(), hasX };
  if (!Array.isArray(raw.abilities)) c.error('abilities', 'abilities must be an array (use []).');
  else raw.abilities.forEach((a, i) => checkAbility(c, a, `abilities[${i}]`, ec));

  for (const t of declaredTokens) {
    if (!ec.usedTokens.has(t)) c.warn('tokens', `Token "${t}" is declared but never created.`);
  }

  if (!isString(raw.text)) c.error('text', 'text is required (use "" only for a basic Wallet).');
  else if (type !== 'wallet' && raw.text.trim().length === 0) {
    c.error('text', 'text must not be empty for a non-Wallet card.');
  }
  if (raw.flavor !== undefined && !isString(raw.flavor)) c.error('flavor', 'flavor must be a string.');

  checkArt(c, raw.art, 'art');
  checkAi(c, raw.ai, 'ai');

  if (raw.legendary !== undefined && typeof raw.legendary !== 'boolean') {
    c.error('legendary', 'legendary must be a boolean.');
  }
  if (!Array.isArray(raw.tags)) c.error('tags', 'tags must be an array (use []).');
  else {
    for (const t of raw.tags) if (!isString(t)) c.error('tags', 'tags must be strings.');
  }

  if (!isObject(raw.legality)) c.error('legality', 'legality is required, e.g. { "genesis": "legal" }.');
  else {
    const formats = Object.keys(raw.legality);
    if (formats.length === 0) c.error('legality', 'legality needs at least one format.');
    for (const f of formats) {
      if (!inSet(raw.legality[f], LEGALITY_VALUES)) {
        c.error(`legality.${f}`, 'legality values must be legal, banned or restricted.');
      }
    }
  }

  if (raw.persona !== undefined) {
    if (!isObject(raw.persona)) c.error('persona', 'persona must be an object.');
    else {
      if (!isString(raw.persona.archetypeOf) || raw.persona.archetypeOf.trim().length === 0) {
        c.error('persona.archetypeOf', 'persona.archetypeOf is required.');
      }
      if (typeof raw.persona.licensed !== 'boolean') {
        c.error('persona.licensed', 'persona.licensed must be a boolean.');
      }
      if (raw.persona.notes !== undefined && !isString(raw.persona.notes)) {
        c.error('persona.notes', 'persona.notes must be a string.');
      }
    }
  }

  // Type-specific requirements.
  if (type === 'character') {
    if (!isNumber(raw.attack) || raw.attack < 0) c.error('attack', 'Characters need a non-negative attack.');
    if (!isNumber(raw.health) || raw.health < 1) c.error('health', 'Characters need a health of at least 1.');
  } else {
    if (raw.attack !== undefined) c.error('attack', 'only Characters have attack.');
    if (raw.health !== undefined) c.error('health', 'only Characters have health.');
  }

  if (type === 'wallet') {
    if (!isObject(raw.wallet)) c.error('wallet', 'Wallets need a wallet block.');
    else {
      const w = raw.wallet;
      if (w.chain !== 'any' && !inSet(w.chain, CHAINS)) {
        c.error('wallet.chain', `Unknown chain "${String(w.chain)}".`);
      }
      if (typeof w.basic !== 'boolean') c.error('wallet.basic', 'wallet.basic must be a boolean.');
      if (w.entersExhausted !== undefined && typeof w.entersExhausted !== 'boolean') {
        c.error('wallet.entersExhausted', 'wallet.entersExhausted must be a boolean.');
      }
      if (w.produces !== undefined && (!isNumber(w.produces) || w.produces < 1)) {
        c.error('wallet.produces', 'wallet.produces must be a positive number.');
      }
    }
  } else if (raw.wallet !== undefined) {
    c.error('wallet', 'only Wallets have a wallet block.');
  }

  if (type === 'upgrade') {
    if (!isObject(raw.upgrade)) c.error('upgrade', 'Upgrades need an upgrade block.');
    else {
      const u = raw.upgrade;
      if (!isNumber(u.attack)) c.error('upgrade.attack', 'upgrade.attack must be a number.');
      if (!isNumber(u.health)) c.error('upgrade.health', 'upgrade.health must be a number.');
      if (!Array.isArray(u.keywords)) c.error('upgrade.keywords', 'upgrade.keywords must be an array.');
      else {
        for (const k of u.keywords) {
          if (!inSet(k, ALL_KEYWORDS)) c.error('upgrade.keywords', `Unknown keyword "${String(k)}".`);
        }
      }
    }
  } else if (raw.upgrade !== undefined) {
    c.error('upgrade', 'only Upgrades have an upgrade block.');
  }

  // A Transaction or Exploit performs its effects through self:deploy abilities.
  if (type === 'transaction' || type === 'exploit') {
    const abilities = Array.isArray(raw.abilities) ? raw.abilities : [];
    const hasSpellBody = abilities.some(
      (a) => isObject(a) && a.kind === 'triggered' && a.event === 'self:deploy',
    );
    if (!hasSpellBody) {
      c.error(
        'abilities',
        'a Transaction or Exploit performs its effects through a triggered ability with event "self:deploy".',
      );
    }
    const hasStatic = abilities.some((a) => isObject(a) && a.kind === 'static');
    if (hasStatic) {
      c.error('abilities', 'a Transaction or Exploit cannot have static abilities, it never stays on the board.');
    }
  }

  // A permanent with text but nothing the engine can run is usually a mistake.
  if (
    isString(raw.text) &&
    raw.text.trim().length > 0 &&
    Array.isArray(raw.abilities) &&
    raw.abilities.length === 0 &&
    Array.isArray(raw.keywords) &&
    raw.keywords.length === 0 &&
    type !== 'wallet'
  ) {
    c.warn('text', 'this card has rules text but no abilities and no keywords.');
  }

  return c.issues;
}

// ---------------------------------------------------------------------------
// Token and deck
// ---------------------------------------------------------------------------

export function validateToken(raw: unknown, where: string, knownTokenIds: string[] = []): Issue[] {
  const c = new Collector(where);
  if (!isObject(raw)) {
    c.error('', 'A token must be a JSON object.');
    return c.issues;
  }
  const id = isString(raw.id) ? raw.id : undefined;
  c.setSubject(id ?? where);
  if (!id || !/^tok-[a-z0-9-]+$/.test(id)) c.error('id', 'token ids look like tok-shitcoin.');
  if (!isString(raw.name) || raw.name.trim().length === 0) c.error('name', 'name is required.');
  if (!inSet(raw.faction, FACTIONS)) c.error('faction', `Unknown faction "${String(raw.faction)}".`);
  if (!Array.isArray(raw.subtypes)) c.error('subtypes', 'subtypes must be an array.');
  else {
    for (const s of raw.subtypes) {
      if (!inSet(s, SUBTYPES)) c.error('subtypes', `Unknown subtype "${String(s)}".`);
    }
  }
  if (!isNumber(raw.attack) || raw.attack < 0) c.error('attack', 'tokens need a non-negative attack.');
  if (!isNumber(raw.health) || raw.health < 1) c.error('health', 'tokens need a health of at least 1.');
  if (!Array.isArray(raw.keywords)) c.error('keywords', 'keywords must be an array.');
  else {
    for (const k of raw.keywords) {
      if (!inSet(k, ALL_KEYWORDS)) c.error('keywords', `Unknown keyword "${String(k)}".`);
    }
  }
  const ec: EffectContext = { declaredTokens: knownTokenIds, usedTokens: new Set(), hasX: false };
  if (!Array.isArray(raw.abilities)) c.error('abilities', 'abilities must be an array (use []).');
  else raw.abilities.forEach((a, i) => checkAbility(c, a, `abilities[${i}]`, ec));
  if (!isString(raw.text)) c.error('text', 'text is required (use "" for a plain body).');
  checkArt(c, raw.art, 'art');
  return c.issues;
}

export function validateDeckDefinition(raw: unknown, where: string, knownCardIds: string[]): Issue[] {
  const c = new Collector(where);
  if (!isObject(raw)) {
    c.error('', 'A deck must be a JSON object.');
    return c.issues;
  }
  const id = isString(raw.id) ? raw.id : undefined;
  c.setSubject(id ?? where);
  if (!id || id.trim().length === 0) c.error('id', 'id is required.');
  if (!isString(raw.name) || raw.name.trim().length === 0) c.error('name', 'name is required.');
  if (!Array.isArray(raw.factions)) c.error('factions', 'factions must be an array.');
  else {
    for (const f of raw.factions) {
      if (!inSet(f, FACTIONS)) c.error('factions', `Unknown faction "${String(f)}".`);
    }
  }
  if (!isString(raw.archetype) || raw.archetype.trim().length === 0) {
    c.error('archetype', 'archetype is required (free text used by bots).');
  }
  if (!Array.isArray(raw.cards)) c.error('cards', 'cards must be an array of { id, count }.');
  else {
    raw.cards.forEach((entry, i) => {
      if (!isObject(entry)) {
        c.error(`cards[${i}]`, 'each entry must be { id, count }.');
        return;
      }
      if (!isString(entry.id)) c.error(`cards[${i}].id`, 'id must be a string.');
      else if (knownCardIds.length > 0 && !knownCardIds.includes(entry.id)) {
        c.error(`cards[${i}].id`, `Unknown card id "${entry.id}".`);
      }
      if (!isNumber(entry.count) || entry.count < 1) {
        c.error(`cards[${i}].count`, 'count must be a positive number.');
      }
    });
  }
  if (raw.dev !== undefined && typeof raw.dev !== 'boolean') c.error('dev', 'dev must be a boolean.');
  return c.issues;
}

// ---------------------------------------------------------------------------
// Whole set
// ---------------------------------------------------------------------------

export interface SetSummaryRow {
  faction: string;
  total: number;
  byType: Record<string, number>;
  byRarity: Record<string, number>;
}

export interface SetValidation {
  issues: Issue[];
  errors: Issue[];
  warnings: Issue[];
  valid: boolean;
  summary: SetSummaryRow[];
  cardCount: number;
  tokenCount: number;
}

export interface RawSetInput {
  /** Card entries with the file they came from, for error messages. */
  cards: { raw: unknown; where: string }[];
  tokens: { raw: unknown; where: string }[];
  decks?: { raw: unknown; where: string }[];
}

export function validateCardSet(input: RawSetInput): SetValidation {
  const issues: Issue[] = [];

  const tokenIds: string[] = [];
  for (const t of input.tokens) {
    if (isObject(t.raw) && isString(t.raw.id)) tokenIds.push(t.raw.id);
  }
  const seenTokens = new Set<string>();
  for (const t of input.tokens) {
    issues.push(...validateToken(t.raw, t.where, tokenIds));
    if (isObject(t.raw) && isString(t.raw.id)) {
      if (seenTokens.has(t.raw.id)) {
        issues.push({
          level: 'error',
          path: 'id',
          message: `Duplicate token id "${t.raw.id}".`,
          subject: t.raw.id,
        });
      }
      seenTokens.add(t.raw.id);
    }
  }

  const seenIds = new Set<string>();
  const seenCollector = new Map<string, string>();
  const cardIds: string[] = [];
  const summary = new Map<string, SetSummaryRow>();

  for (const entry of input.cards) {
    issues.push(...validateCard(entry.raw, entry.where, { knownTokenIds: tokenIds }));
    if (!isObject(entry.raw)) continue;
    const raw = entry.raw;
    const id = isString(raw.id) ? raw.id : entry.where;
    cardIds.push(id);
    if (seenIds.has(id)) {
      issues.push({ level: 'error', path: 'id', message: `Duplicate card id "${id}".`, subject: id });
    }
    seenIds.add(id);

    if (isString(raw.set) && isNumber(raw.collectorNumber)) {
      const key = `${raw.set}#${raw.collectorNumber}`;
      const prev = seenCollector.get(key);
      if (prev) {
        issues.push({
          level: 'error',
          path: 'collectorNumber',
          message: `Collector number ${raw.collectorNumber} in set ${raw.set} is already used by ${prev}.`,
          subject: id,
        });
      } else {
        seenCollector.set(key, id);
      }
    }

    const faction = isString(raw.faction) ? raw.faction : 'unknown';
    const row = summary.get(faction) ?? { faction, total: 0, byType: {}, byRarity: {} };
    row.total++;
    const type = isString(raw.type) ? raw.type : 'unknown';
    const rarity = isString(raw.rarity) ? raw.rarity : 'unknown';
    row.byType[type] = (row.byType[type] ?? 0) + 1;
    row.byRarity[rarity] = (row.byRarity[rarity] ?? 0) + 1;
    summary.set(faction, row);
  }

  for (const d of input.decks ?? []) {
    issues.push(...validateDeckDefinition(d.raw, d.where, cardIds));
  }

  const errors = issues.filter((i) => i.level === 'error');
  const warnings = issues.filter((i) => i.level === 'warning');
  const order = ['btc', 'eth', 'sol', 'bnb', 'xrp', 'neutral'];
  return {
    issues,
    errors,
    warnings,
    valid: errors.length === 0,
    summary: [...summary.values()].sort(
      (a, b) => order.indexOf(a.faction) - order.indexOf(b.faction),
    ),
    cardCount: input.cards.length,
    tokenCount: input.tokens.length,
  };
}

/** Narrow a validated raw object. Only call after validateCard reported no errors. */
export function asCard(raw: unknown): CardDefinition {
  return raw as CardDefinition;
}

export function asToken(raw: unknown): TokenDefinition {
  return raw as TokenDefinition;
}

export function asDeck(raw: unknown): DeckDefinition {
  return raw as DeckDefinition;
}
